import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, DollarSign, TrendingUp, Calendar, Wallet, ArrowUpLeft, ArrowDownRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const DriverEarnings: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const driver = currentUser as any;
  
  const [activeTab, setActiveTab] = useState<'today' | 'week' | 'month'>('today');

  const earnings = driver?.earnings || { today: 0, week: 0, month: 0, total: 0 };

  const transactions = [
    { id: 1, type: 'earning', amount: 1200, description: 'رحلة #1234', date: '2024-01-15', time: '14:30' },
    { id: 2, type: 'earning', amount: 850, description: 'رحلة #1233', date: '2024-01-15', time: '12:15' },
    { id: 3, type: 'earning', amount: 1500, description: 'رحلة #1230', date: '2024-01-14', time: '18:45' },
    { id: 4, type: 'withdrawal', amount: 5000, description: 'سحب إلى بنكك', date: '2024-01-14', time: '09:00' },
    { id: 5, type: 'earning', amount: 2000, description: 'رحلة #1228', date: '2024-01-13', time: '16:20' },
  ];

  const getEarningsByTab = () => {
    switch (activeTab) {
      case 'today': return earnings.today;
      case 'week': return earnings.week;
      case 'month': return earnings.month;
      default: return earnings.today;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/driver/home')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">أرباحي</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Total Earnings Card */}
        <div className="bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl p-6 text-white mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <span className="text-white/70">إجمالي الأرباح</span>
          </div>
          <p className="text-4xl font-bold mb-2">{earnings.total.toLocaleString()} ج.س</p>
          <p className="text-white/70 text-sm">منذ بدء العمل مع رحلة</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'today', label: 'اليوم' },
            { key: 'week', label: 'هذا الأسبوع' },
            { key: 'month', label: 'هذا الشهر' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-secondary-500 text-white'
                  : 'bg-white text-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Period Earnings */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 mb-1">
                {activeTab === 'today' && 'أرباح اليوم'}
                {activeTab === 'week' && 'أرباح هذا الأسبوع'}
                {activeTab === 'month' && 'أرباح هذا الشهر'}
              </p>
              <p className="text-3xl font-bold text-secondary-600">
                {getEarningsByTab().toLocaleString()} ج.س
              </p>
            </div>
            <div className="w-14 h-14 bg-secondary-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-secondary-600" />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">عدد الرحلات</p>
            <p className="text-2xl font-bold">{driver?.totalRides || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-gray-500 text-sm mb-1">متوسط الرحلة</p>
            <p className="text-2xl font-bold">
              {driver?.totalRides ? Math.round(earnings.total / driver.totalRides) : 0} ج.س
            </p>
          </div>
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-4 border-b">
            <h2 className="font-bold">سجل المعاملات</h2>
          </div>
          <div className="divide-y">
            {transactions.map((txn) => (
              <div key={txn.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    txn.type === 'earning' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                  }`}>
                    {txn.type === 'earning' ? <ArrowDownRight className="w-5 h-5" /> : <ArrowUpLeft className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-medium">{txn.description}</p>
                    <p className="text-gray-500 text-sm">{txn.date} • {txn.time}</p>
                  </div>
                </div>
                <p className={`font-bold ${txn.type === 'earning' ? 'text-green-600' : 'text-red-600'}`}>
                  {txn.type === 'earning' ? '+' : '-'}{txn.amount} ج.س
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Withdraw Button */}
        <button className="w-full mt-6 bg-secondary-500 text-white py-4 rounded-xl font-bold hover:bg-secondary-600 transition-colors flex items-center justify-center gap-2">
          <Wallet className="w-5 h-5" />
          سحب الأرباح
        </button>
      </main>
    </div>
  );
};

export default DriverEarnings;
